import { useMemo } from "react";
import { CustomersStepTypeEnumPaths } from "@/shared/endpoints/customers";
import { ITestClaimSlot } from "@/shared/helpers/test-claims-combo";
import { useAppSelector } from "@/shared/hooks/store";
import { useChatClassName } from '@/shared/hooks/useChatClassName';
import { selectOnboardingUser } from "@/store/slices/onboarding/selectors";
import { IPrescribables } from "@/shared/helpers/main";
import { Alert, Button } from "@/shared/ui";
import { HueOption } from "@/modules/onboarding/constants";
import { URLSearchParamsInit, useNavigate } from "react-router-dom";
import { ROUTES } from "@/shared/constants/routes";
import ShippingAddressBlock from "./shipping-address-block";

import _ from "lodash";

import './style.css';

interface IProps {
    claims: Record<string, ITestClaimSlot>;
    showCopayLoading: boolean;
    onShowCopay?(): void;
    canRunClaimsBasedOnInsurancesInProfile: boolean;
    navigateToStep?: (step: CustomersStepTypeEnumPaths, index?: number, searchParams?: URLSearchParamsInit) => void;
    copayRequestsExhausted?: boolean;
    prescribables: IPrescribables;
    showCopay: boolean;
    hasMissingCopayAmount: boolean;
}

const ReviewMainInfo = ({
    copayRequestsExhausted = false,
    navigateToStep,
    hasMissingCopayAmount
}: IProps) => {
    const navigate = useNavigate();
    const user = useAppSelector(selectOnboardingUser);
    const chatClassName = useChatClassName();

    const selectedHue = useMemo(() => (
        user && HueOption.find(hue => hue.id === user.userOnboarding?.details?.medboxHueType)
    ), [user]);

    const onHueColorChange = () => {
        if (navigateToStep) navigateToStep(CustomersStepTypeEnumPaths.PRESCRIPTIONS, -1, { fromReview: '1' });
        else {
            navigate(
                `/${ROUTES.ONBOARDING.MAIN}/${ROUTES.ONBOARDING.PRESCRIPTIONS.MAIN}/${ROUTES.ONBOARDING.PRESCRIPTIONS.MEDBOX_HUE}?from=review`
            );
        }
    };

    const AlertUi = useMemo(() => {
        if (copayRequestsExhausted && hasMissingCopayAmount) {
            return (
                <Alert
                    variant="warning"
                    className="mt-[1.625rem]"
                    label={
                        <div className="flex flex-col items-start">
                            <span className="leading-[137%] text-15 font-atkinsonHyperlegible font-normal md:text-16">
                                <span className="font-atkinsonBold font-bold">It looks like you’ve reached the maximum usage limit for showing copays.</span>
                                &nbsp;Still have questions? Our support team is available to assist.
                            </span>

                            <Button
                                variant="text"
                                sx={{ mt: '0.625rem' }}
                                className={chatClassName}
                            >
                                <span className="font-dmSansMedium font-bold text-17 md:text-lg">Chat Support</span>
                            </Button>
                        </div>

                    }
                />
            )
        }
        return null;
    }, [copayRequestsExhausted, hasMissingCopayAmount, chatClassName]);

    if (!user) return null;
    //  {canRunClaimsBasedOnInsurancesInProfile && !showCopay && (!copayRequestsExhausted || !hasMissingCopayAmount) && (
    return (
        <div className="flex flex-col md:items-start">
            <div className="w-full flex flex-col md:items-start">
                <div className="w-full flex justify-between">
                    <div className="w-1/2">
                        <h4 className="ph-no-capture font-atkinsonHyperlegible text-25 sm:text-35 leading-[130%]">
                            {`${user?.userProfile?.firstName} ${user?.userProfile?.lastName}`}’s MedBox
                        </h4>
                    </div>

                    <div className="box-border h-max overflow-hidden w-auto">
                        <div className="relative w-[6.5rem] h-[6.5rem] rounded-10 overflow-hidden sm:w-[7.8rem] sm:h-[7.8rem] ml-auto">
                            <img src={selectedHue?.src} className="w-full h-full relative object-center" />
                            <button
                                className="w-[calc(100%-16px)] absolute left-2 bottom-2 z-10 bg-primary-1400 rounded-full flex flex-col justify-center items-center py-1 sm:py-1 hover:border-0"
                                onClick={onHueColorChange}
                            >
                                <span className="font-atkinsonHyperlegible text-16 block text-white">Edit Color</span>
                            </button>
                        </div>
                    </div>
                </div>

                <ShippingAddressBlock addresses={user.addresses || []} />
            </div>
            {AlertUi}
        </div >
    );
}

export default ReviewMainInfo;